import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService, PrimeIcons } from 'primeng/api';
import { AppIcons } from '../../../shared/app.icons';
import { AppSettings } from '../../../shared/app.settings';
import { MITableComponent } from '../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../shared/models/country';
import type { Language } from '../../../shared/models/language';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import type { EntityCountsResponse, EntityList } from '../../models/entity.models';

import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { ModuleHeaderComponent } from '../../../shared/components/module-header/module-header.component';
import { ConfigService } from '../../../shared/services/config.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import dayjs from 'dayjs';
import { MITableService } from '../../../shared/components/mi-table/mitable.service';
import { DownloadService } from '../../../shared/services/download.service';
import { ButtonModule } from 'primeng/button';
import { StorageService } from 'app/modules/shared/services/storage.service';

@Component({
  selector: 'app-orders-list',
  standalone: true,
  imports: [BreadcrumbModule,
    ButtonModule,
    ModuleHeaderComponent, TabViewModule, TranslateModule, MITableComponent, MITableComponent, AccessProviderDirective],
  templateUrl: './orders-list.component.html',
  styleUrl: './orders-list.component.scss'
})
export class OrdersListComponent implements OnInit {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns = [];
  cols = [];
  moduleName = 'order';
  filterList;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels = {};
  filterAttributeLabels = {};
  listColumns: any;
  allColumns: any;
  pagination: any;
  totalRecords: number = 0;
  tableData: any = [];
  buttons: any[] = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    advanceFilters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ""
  };
  routePath: MenuItem[] = [];
  deleteOrderMsg: string;
  deleteOrderHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: any[];
  isReady: boolean = false;
  attributeData: any;
  data;
  rowItems: MenuItem[] | undefined;
  rowItemsCompleted: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader;
  reviewHeader: any;
  btnUpdate: any;
  entityCreatedAtAttributeId;
  regionGeofences: any = [];
  zoneIds: any = [];
  isEntity = true;
  orderStatusAttributeId: string;
  showImportExportBtn: boolean = false;
  selectedOrder: any;

  isListAllAllowed: boolean = false;
  isListActiveAllowed: boolean = false;
  isListToBeInvoicedAllowed: boolean = false;
  isListDeletedAllowed: boolean = false;
  isListCompletedAllowed: boolean = false;
  isListRejectedAllowed: boolean = false;
  isAddOrderAllowed: boolean = false;
  userDetailsById: any[] = [];
  btnDownloadInvoice: string = this.cs.getLabel('order.download_invoice');
  usersData: any[] = [];
  parentOrderId;
  relatedOrderId;
  relatedOrderNumber;

  private exportInProgress = false;
  private metaDataLoaded = false;
  private tabFromRouteLoaded = false;
  private isTabChangeInternal = false;

  constructor(private orderService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService,
    private miTableService: MITableService, private downloadService: DownloadService,
    private storageService: StorageService,
  ) { }

  ngOnInit() {
    this.miTableService.selectedRows.next([]);
    this.initializeLabels();
    this.initializeRowItems();
    this.initializeOtherVariablesAndAPI();
    this.getMetaData();
    this.setTabFromRoute();
    this.setTabPermissions();
    this.restoreActiveTab();
    this.handleUrlParams();
  }

  exportData(selectedRows: any[]) {
    if (!selectedRows || selectedRows.length === 0) {
      this.messageService.add({
        key: 'tst',
        severity: 'warn',
        summary: 'Warning',
        detail: this.cs.getLabel('order.no_orders_selected')
      });
      return;
    }

    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      orderIds: selectedRows
    };

    this.loaderService.show();

    if (this.activeIndex === 2) {
      this.orderService.markOrderCompleteAndSendEmail(AppSettings.ENTITY_CODE.ORDER, requestBody).subscribe({
        next: () => {
          this.loaderService.hide();
          this.messageService.add({
            key: 'tst',
            severity: 'success',
            summary: 'Success',
            detail: this.cs.getLabel('order.mark_as_completed_and_download_invoice_successful')
          });

          setTimeout(() => {
            this.searchEntity();
            this.getEntityCount();
            this.tableRef.clearSelected();
            this.miTableService.selectedRows.next([]);
          }, 300);
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.cs.getLabel('order.download_invoice_failed')
          });
        }
      });
    } else {
      this.orderService.exportOrdersInvoiceData(AppSettings.ENTITY_CODE.ORDER, requestBody).subscribe({
        next: (res) => {
          this.loaderService.hide();
          if (res && this.downloadService.isValidResponse(res)) {
            try {
              this.downloadService.downloadZip(res, 'orders_invoice');

              this.messageService.add({
                key: 'tst',
                severity: 'success',
                summary: 'Success',
                detail: this.cs.getLabel('order.download_invoice_successful')
              });

              setTimeout(() => {
                this.searchEntity();
                this.getEntityCount();
                this.tableRef.clearSelected();
                this.miTableService.selectedRows.next([]);
              }, 300);
            } catch (error) {
              this.messageService.add({
                key: 'tst',
                severity: 'error',
                summary: 'Error',
                detail: this.cs.getLabel('order.download_invoice_failed')
              });
            }
          } else {
            this.messageService.add({
              key: 'tst',
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('order.invalid_response')
            });
          }
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.cs.getLabel('order.download_invoice_failed')
          });
        }
      });
    }
  }

  exportSelectedOrders() {
    if (this.exportInProgress) return;
    this.exportInProgress = true;
    setTimeout(() => {
      this.exportInProgress = false;
    }, 500);

    const selectedRows = this.miTableService.selectedRows.value?.map(row => row.id) || [];

    if (!selectedRows.length) {
      this.messageService.add({
        key: 'tst',
        severity: 'warn',
        summary: 'Warning',
        detail: this.cs.getLabel('order.no_orders_selected')
      });
      return;
    }

    this.exportData(selectedRows);
  }

  setTabPermissions() {
    this.isListAllAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'ALL', 'LIST');
    this.isAddOrderAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'ALL', 'ADD');
    this.isListActiveAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'ACTIVE', 'LIST');
    this.isListToBeInvoicedAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'TO_BE_INVOICED', 'LIST');
    this.isListCompletedAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'COMPLETED', 'LIST');
    this.isListRejectedAllowed = this.permissionAccessService.hasAccess('ORDERS', '', 'REJECTED', 'LIST');
  }

  getMetaData() {
    this.getEntityCount();
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setOrderFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setOrderTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)
      this.metaDataLoaded = true;
      this.tryInitialSearchEntity();
    });
  }

  private getMultiApiPayload() {

    const tableView = this.configService.isSiteUser() ? AppSettings.VIEW_CODE.ORDER_LIST_ON_SITE_ADMIN : AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW;
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${tableView}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  private initializeRowItems() {
    if (this.configService.isAdmin()) {
      this.rowItems = [{
        label: this.cs.getLabel('order.mark_as_completed_and_download_invoice'),
        icon: PrimeIcons.SEND,
        iconClass: 'bulk-update-icon',
        value: 'download_invoice',
        command: () => {
          this.exportSingleOrder(this.currentRow.id, true);
        }
      }];

      this.rowItemsCompleted = [{
        label: this.cs.getLabel('order.download_invoice'),
        icon: `${AppIcons.DOWNLOAD} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'download_invoice',
        command: () => {
          this.exportSingleOrder(this.currentRow.id);
        }
      }];
    }
  }

  exportSingleOrder(orderId: string, markAsCompleted: boolean = false) {

    this.loaderService.show();

    if (markAsCompleted) {
      const requestData = {
        forTenantCode: this.configService.getForTenantCode(),
        orderIds: [orderId]
      };

      this.orderService.markOrderCompleteAndSendEmail(AppSettings.ENTITY_CODE.ORDER, requestData).subscribe({
        next: () => {
          this.loaderService.hide();
          this.searchEntity();
          this.getEntityCount();
          this.tableRef.clearSelected();
          this.messageService.add({
            key: 'tst',
            severity: 'success',
            summary: 'Success',
            detail: this.cs.getLabel('order.mark_as_completed_and_download_invoice_successful')
          });
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.cs.getLabel('order.download_invoice_failed')
          });
        }
      });
    } else {
      this.orderService.exportSingleOrder(AppSettings.ENTITY_CODE.ORDER, orderId).subscribe({
        next: (res) => {
          this.loaderService.hide();
          if (res) {
            try {
              this.downloadService.downloadPdf(res, 'order_invoice');
              this.searchEntity();
              this.getEntityCount();
              this.tableRef.clearSelected();
              this.messageService.add({
                key: 'tst',
                severity: 'success',
                summary: 'Success',
                detail: this.cs.getLabel('order.download_invoice_successful')
              });
            } catch (error) {
              this.messageService.add({
                key: 'tst',
                severity: 'error',
                summary: 'Error',
                detail: this.cs.getLabel('order.download_invoice_failed')
              });
            }
          }
        },
        error: () => {
          this.loaderService.hide();
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.cs.getLabel('order.download_invoice_failed')
          });
        }
      });
    }
  }

  onUpdateStatus(status) {
    const isActive = status === 'Active';
    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'order.lbl_activate_confirmation' : 'order.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'order.msg_activate_confirmation' : 'order.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.cs.getLabel(isActive ? 'order.lbl_activate' : 'order.lbl_deactivate'),
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const orderData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.orderStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.orderService.saveAttributeData(orderData.entityCode, this.currentRow.id, orderData).subscribe({
          next: () => {
            this.messageService.add({
              key: 'tst',
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('order.lbl_activate_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
              this.getEntityCount();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              key: 'tst',
              severity: 'error',
              summary: this.cs.getLabel('order.lbl_failed_status'),
              detail: this.cs.getLabel('order.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }

  onUpdateActionStatus(status) {
    const isArchive = status === 'ARCHIVE';
    this.confirmationService.confirm({
      header: this.cs.getLabel(isArchive ? 'order.lbl_archive_confirmation' : 'order.lbl_unarchive_confirmation'),
      message: this.cs.getLabel(isArchive ? 'order.msg_archive_confirmation' : 'order.msg_unarchive_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.cs.getLabel('order.lbl_archive_dialog'),
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const requestBody = {
          forTenantCode: this.configService.getForTenantCode(),
          countryCode: AppSettings.HEADER_COUNTRY_CODE,
          entityCode: AppSettings.ENTITY_CODE.ORDER,
          actionStatus: status
        };

        this.orderService.updateStatus(this.currentRow.id, requestBody).subscribe({
          next: () => {
            this.messageService.add({
              key: 'tst',
              severity: 'success',
              summary: 'Success',
              detail: status === 'ARCHIVE' ?
                this.cs.getLabel('order.lbl_order_archived_successfully') :
                this.cs.getLabel('order.lbl_order_unarchived_successfully')
            });
            setTimeout(() => {
              this.searchEntity();
              this.getEntityCount();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              key: 'tst',
              severity: 'error',
              summary: 'Error',
              detail: status === 'ARCHIVE' ?
                this.cs.getLabel('order.lbl_order_archive_failed') :
                this.cs.getLabel('order.lbl_order_unarchive_failed')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.bulkUpdateHeader = this.cs.getLabel('lbl_update_orders');
    this.reviewHeader = this.cs.getLabel('lbl_review_orders');
    this.btnUpdate = this.cs.getLabel('btn_update_orders2');
    this.routePath = [{ label: this.cs.getLabel('order.lbl_orders'), routerLink: 'orders/list' }];
    this.btnDownloadInvoice = this.activeIndex === 2 ? this.cs.getLabel('order.mark_as_completed_and_download_invoice') : this.cs.getLabel('order.download_invoice');
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('order.add_order');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteOrderMsg = this.cs.getLabel('order.message.confirm_delete_msg');
    this.deleteOrderHeaderMsg = this.cs.getLabel('order.message.confirm_delete_header');
  }

  public onChange(event) {
    for (const file of event.target.files) {
      if (file) {
        if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
          this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append('forTenantCode', this.configService.getForTenantCode());
          formData.append('entityCode', AppSettings.ENTITY_CODE.ORDER);
          formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
          this.cs.importEntity(formData, AppSettings.ENTITY_CODE.ORDER).subscribe(() => {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_uploaded_successfully') });
          })
        }
      }
    }
  }

  setCurrentRowData(event) {
    this.currentRow = event;

    if (event.deleted) {
      this.rowItems.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }

    this.rowItems.forEach(item => {
      switch (item.value) {
        case 'download_invoice':
          item.disabled = event.actionStatus !== 'TO_BE_INVOICED' && event.actionStatus !== 'COMPLETED';
          break;
      }
    });
  }

  update() {
    this.onUpdateOrder(this.currentRow.id);
  }


  setOrderTableView(res) {
    this.isLoading = false;
    this.data = res;
    this.orderStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'order_status')?.attributeId;

    this.attributeLabels = this.cs.getAttributeLabels(this.data);

    this.allColumns = this.cs.getColumns(this.data);

    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      if (this.configService.isSiteUser()) {
        if (key.attributeCode === 'total_units') {
          this.allColumns[index].header = this.cs.getLabel('order.qty_ordered')
        } else if (key.attributeCode === 'created_by') {
          this.allColumns[index].header = this.cs.getLabel('order.ordered_by')
        } else if (key.attributeCode === 'expected_delivery_date') {
          this.allColumns[index].header = this.cs.getLabel('order.lbl_delivery_date');
        } else {
          this.allColumns[index].header = this.cs.getLabelValue(
            `order` + `.fields.${key.attributeCode}.label`,
            this.attributeLabels,
            key.attributeCode
          );
        }
      } else {
        if (key.attributeCode === 'expected_delivery_date') {
          this.allColumns[index].header = this.cs.getLabel('order.lbl_delivery_date');
        } else {
          this.allColumns[index].header = this.cs.getLabelValue(
            `order` + `.fields.${key.attributeCode}.label`,
            this.attributeLabels,
            key.attributeCode
          );
        }
      }

    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  getFilterView() {
    this.orderService.getAttributeDefinition(AppSettings.ENTITY_CODE.ORDER, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe(filterResponse => {
      if (filterResponse) {
        this.setOrderFilterView(filterResponse);
      }
    });
  }

  setOrderFilterView(filterResponse) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse);
    this.filterList = this.cs.getColumns(filterResponse);
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    this.filterList.filter((field: { attributeCode: string;[key: string]: unknown }) => {

      if (field.attributeCode === 'protocol_id') {
        field.moduleName = AppSettings.ENTITY_CODE.PROTOCOL;
        return true;
      }

      if (field.attributeCode === 'entity_created_at') {
        field.inputCode = 'dateRange';
        return true;
      }
    });

    if (this.configService.isSiteUser()) {
      this.filterList = this.filterList.filter(field => field.attributeCode !== 'site_id');
    }
  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    const tableView = this.configService.isSiteUser() ? AppSettings.VIEW_CODE.ORDER_LIST_ON_SITE_ADMIN : AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW;

    this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
    this.entityData.tableViewCode = tableView;
    this.loaderService.show();
    this.orderService.searchEntity(AppSettings.ENTITY_CODE.ORDER, this.entityData, false).subscribe((res: any) => {
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;

      const rawData = res.data.map((data: any) => {
        const flatRelatedValues = (data.relatedData || []).reduce((acc: any, item: any) => {
          if (item.entityCode !== 'order') {
            return {
              ...acc,
              ...(item.values || {})
            };
          }
          return acc;
        }, {});

        const orderData = (data.relatedData || []).filter(item => item.entityCode === 'order')
          .map(item => ({
            id: item.id,
            ...(item.values || {})
          }));
        return {
          id: data.id,
          actionStatus: data.actionStatus,
          ...(data.values || {}),
          ...flatRelatedValues,
          order: orderData
        };
      });

      if (this.configService.isSiteUser()) {
        const orderIds = rawData.map(data => data.created_by);
        if (orderIds.length > 0) {
          this.getUserDetailsById(orderIds, rawData);
        } else {
          this.tableData = this.applySiteOrderStatusLabels(rawData);
          this.loaderService.hide();
        }
      } else {
        this.tableData = rawData;
        this.loaderService.hide();
      }
    })
  }

  private applySiteOrderStatusLabels(data: any[]): any[] {
    if (!this.configService.isSiteUser()) {
      return data;
    }

    const siteStatusMap: Record<string, string> = {
      'New Order': 'Order Submitted',
      'Order Received': 'Order Delivered',
    };

    return data.map(row => {
      if (!row?.order_status || typeof row.order_status !== 'object') {
        return row;
      }

      const mappedOrderStatus = Object.entries(row.order_status).reduce((acc, [key, value]) => {
        acc[key] = siteStatusMap[value as string] ?? value;
        return acc;
      }, {} as Record<string, unknown>);

      return { ...row, order_status: mappedOrderStatus };
    });
  }

  getUserDetailsById(userIds: string[], rawData?: any[]) {
    this.orderService.getUserDetailsById(userIds).subscribe(res => {
      this.usersData = res as any[];
      this.userDetailsById = this.usersData.map((user: any) => {
        const fullName = `${user.firstName} ${user.lastName}`;

        const roleName = user.rolelist && user.rolelist.length > 0
          ? user.rolelist[0]?.roleName || '--'
          : '--';

        return {
          userId: user.userId,
          fullName,
          roleName
        };
      });

      const dataToProcess = rawData || this.tableData;
      this.tableData = this.applySiteOrderStatusLabels(dataToProcess.map(order => {
        const userDetail = this.userDetailsById.find(user => user.userId === order.created_by);
        if (userDetail) {
          return {
            ...order,
            created_by: `By ${userDetail.fullName} (${userDetail.roleName})`
          };
        }
        return order;
      }));

      this.loaderService.hide();
    });
  }

  onAddOrder() {
    this.router.navigate(['app/orders/add-order',]);
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onOrderDelete(event) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.ORDER,
      entityIds: [
        event
      ]
    };
    this.orderService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'order', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
          this.getEntityCount();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'order', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  onUpdateOrder(orderId) {
    this.router.navigate([`app/orders/add-order/${orderId}`]);
  }

  onRowSelect(event) {
    if (event.type === 'row') {
      this.storageService.setItem('orderActiveIndex', this.activeIndex);
      const tabParam = this.getTabParamFromIndex(this.activeIndex);
      this.router.navigate(['app/orders/order-details'], {
        queryParams: {
          id: event.data.id,
          fromTab: tabParam
        }
      });
      this.orderService.entityCurrentRow.next(event.data);
    }
  }

  onFilterValueChange(event) {
    const vData = event;
    this.entityData.filters = [];
    this.entityData.advanceFilters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {

        if (key === 'entity_created_at') {
          this.entityData.advanceFilters.push({
            attributeId: this.entityCreatedAtAttributeId,
            fromValue: this.metaDataService.setDateFilterOffset(dayjs(value[0]).startOf('day').valueOf()),
            toValue: this.metaDataService.setDateFilterOffset(dayjs(value[1]).endOf('day').valueOf()),
            comparisonOperator: AppSettings.COMPARISON_OPERATOR.BETWEEN
          });
        } else {
          this.entityData.filters.push({
            attributeCode: key,
            attributeValue
          });
        }
      }
    }

    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  getEntityCount() {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const entityCountPayload = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.ORDER,
      countryCode: <string>country[0].countryCode,
    };

    this.orderService.getEntityCount(AppSettings.ENTITY_CODE.ORDER, entityCountPayload, false).subscribe((res: EntityCountsResponse) => {
      this.entityCount = res;
    })
  }

  onRefreshList() {
    this.searchEntity();
    this.getEntityCount();
  }

  tabViewChange(event) {
    if (this.isTabChangeInternal) {
      this.isTabChangeInternal = false;
      return;
    }
    this.activeIndex = event.index;
    this.storageService.setItem('orderActiveIndex', event.index);
    this.updateUrlWithTab(event.index);
    this.btnDownloadInvoice = this.activeIndex === 2 ? this.cs.getLabel('order.mark_as_completed_and_download_invoice') : this.cs.getLabel('order.download_invoice');
    this.activeIndexChange(event.index);
    this.getEntityCount();
    switch (event.index) {
      case 0:
        this.entityData.actionStatus = "";
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        if (this.tableRef) {
          this.tableRef.clearFilter();
        }
        if (!event.isInit) this.searchEntity();
        break;
      case 1:
        this.entityData.actionStatus = AppSettings.ORDER_STATUS.ACTIVE;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        if (this.tableRef) {
          this.tableRef.clearFilter();
        }
        if (!event.isInit) this.searchEntity();
        break;
      case 2:
        this.entityData.actionStatus = AppSettings.ORDER_STATUS.TO_BE_INVOICED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        if (this.tableRef) {
          this.tableRef.clearFilter();
        }
        if (!event.isInit) this.searchEntity();
        break;
      case 3:
        this.entityData.actionStatus = AppSettings.ORDER_STATUS.COMPLETED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        if (this.tableRef) {
          this.tableRef.clearFilter();
        }
        if (!event.isInit) this.searchEntity();
        break;
      case 4:
        this.entityData.actionStatus = AppSettings.ORDER_STATUS.REJECTED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        if (this.tableRef) {
          this.tableRef.clearFilter();
        }
        if (!event.isInit) this.searchEntity();
        break;
      default:
        break;
    }
    if (event.index === 0 || event.index === 1 || event.index === 2) {
      this.showBulkUpdateDialog = false;
    }
  }

  private restoreActiveTab() {
    const savedIndex = this.storageService.getItem('orderActiveIndex');
    if (savedIndex !== null && savedIndex !== undefined) {
      this.activeIndex = parseInt(savedIndex);
      this.tabViewChange({ index: this.activeIndex });
    }
  }

  private handleUrlParams() {
    this.actRoute.queryParams.subscribe(params => {
      const tabParam = params.tab;
      if (tabParam) {
        const tabIndex = this.getTabIndexFromParam(tabParam);
        if (tabIndex !== -1 && tabIndex !== this.activeIndex) {
          this.activeIndex = tabIndex;
          this.tabViewChange({ index: this.activeIndex });
        }
      }
    });
  }

  private getTabIndexFromParam(tabParam: string): number {
    switch (tabParam.toLowerCase()) {
      case 'all':
        return 0;
      case 'active':
        return 1;
      case 'to-be-invoiced':
        return 2;
      case 'completed':
        return 3;
      case 'rejected':
        return 4;
      default:
        return -1;
    }
  }

  private updateUrlWithTab(tabIndex: number) {
    const tabParam = this.getTabParamFromIndex(tabIndex);
    if (tabParam) {
      this.router.navigate([], {
        relativeTo: this.actRoute,
        queryParams: { tab: tabParam },
        queryParamsHandling: 'merge'
      });
    }
  }

  private getTabParamFromIndex(tabIndex: number): string {
    switch (tabIndex) {
      case 0:
        return 'all';
      case 1:
        return 'active';
      case 2:
        return 'to-be-invoiced';
      case 3:
        return 'completed';
      case 4:
        return 'rejected'
      default:
        return '';
    }
  }

  onBulkDataUpdateEvent() {
  }

  onBulkDataDeleteEvent(event) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.ORDER,
      entityIds: event
    };
    this.orderService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'order', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
          this.getEntityCount();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'order', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    });
  }

  private getStatusForCurrentTab(): string {
    switch (this.activeIndex) {
      case 0:
        return AppSettings.ORDER_STATUS.ALL;
      case 1:
        return AppSettings.ORDER_STATUS.ACTIVE;
      case 2:
        return AppSettings.ORDER_STATUS.TO_BE_INVOICED;
      case 3:
        return AppSettings.ORDER_STATUS.COMPLETED;
      case 4:
        return AppSettings.ORDER_STATUS.REJECTED;
      default:
        return AppSettings.ORDER_STATUS.ALL;
    }
  }


  activeIndexChange(event) {
    if (this.activeIndex === event) return;
    this.activeIndex = event;
    this.isTabChangeInternal = true;
    if (event === 0) {
      this.router.navigate(['app/orders'], { queryParams: { status: 'all' } });
    } else if (event === 1) {
      this.router.navigate(['app/orders'], { queryParams: { status: 'pending' } });
    } else if (event === 2) {
      this.router.navigate(['app/orders'], { queryParams: { status: 'to be invoiced' } });
    } else if (event === 3) {
      this.router.navigate(['app/orders'], { queryParams: { status: 'completed' } });
    } else if (event === 4) {
      this.router.navigate(['app/orders'], { queryParams: { status: 'rejected' } });
    }
  }

  private setTabFromRoute() {
    this.actRoute.queryParams.subscribe(params => {
      const status = params.status;
      let newIndex = 0;
      switch (status) {
        case 'all':
          newIndex = 0;
          break;
        case 'pending':
          newIndex = 1;
          break;
        case 'to be invoiced':
          newIndex = 2;
          break;
        case 'completed':
          newIndex = 3;
          break;
        case 'rejected':
          newIndex = 4;
          break;
        default:
          newIndex = 0;
      }
      if (this.activeIndex !== newIndex) {
        this.isTabChangeInternal = true;
        this.activeIndex = newIndex;
        this.tabViewChange({ index: this.activeIndex, isInit: true });
      }
      this.tabFromRouteLoaded = true;
      this.tryInitialSearchEntity();
    });
  }

  private tryInitialSearchEntity() {
    if (this.metaDataLoaded && this.tabFromRouteLoaded) {
      this.searchEntity();
    }
  }
}






